import { Component, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { assertNotNull } from '@core/prelude';
import { ContactSummary } from '@features/contacts/models';
import { Individual } from '@features/contacts/models/individual';
import { IndividualService } from '@features/contacts/services/individual.service';
import { ModalSetting } from '@shared/components/modal/modal-setting';
import { DatesUtil } from '@shared/utilities/dates/dates.util';

import { TagEntityType } from '@features/opportunities/services/tag.service';
import { IndividualEditComponent } from '../details/edit/individual-edit.component';

type LoadContent = {
  individual: Individual.Model;
  contacts: ContactSummary[];
};

@Component({
  selector: 'spark-contacts-individual-summary',
  templateUrl: './individual-summary.component.html',
  styleUrls: ['./individual-summary.component.scss']
})
export class IndividualSummaryComponent {
  TagEntityType = TagEntityType;
  @Input() refresh$: BehaviorSubject<null> = new BehaviorSubject(null);
  @Input() individualId!: string;

  contacts: ContactSummary[] = [];

  private individualId$: Observable<string> = this.route.paramMap.pipe(
    map((params) => params.get('individualId')),
    assertNotNull(
      'Internal error: IndividualDisplayComponent expects individualId parameter in route'
    ),
    tap((id) => (this.individualId = id)),
    shareReplay(1)
  );

  load$: Observable<LoadContent> = combineLatest([
    this.refresh$,
    this.individualId$
  ]).pipe(
    switchMap(([_, individualId]) =>
      this.individualService.getIndividualWithSpecificContacts(individualId)
    )
  );

  private modal: BsModalRef | null = null;
  citizenLabel = $localize`:Citizen Label|Name of the citizen of a region@@citizen-label:Australian citizen`;

  constructor(
    private modalService: BsModalService,
    private route: ActivatedRoute,
    private individualService: IndividualService
  ) {}

  public edit(): void {
    this.openFormModal(IndividualEditComponent);
  }

  public ageDisplay(date: string | null): string {
    const d = DatesUtil.convertToDate(date);
    return d == null ? '' : `${DatesUtil.getAge(d)}`;
  }

  public displayFurtherDetails(individual: Individual.Model | null): boolean {
    return individual == null
      ? false
      : this.displayBooleanField(individual.citizen) ||
          this.displayBooleanField(individual.permanentResident) ||
          this.displayBooleanField(individual.repayingStudentLoan);
  }

  public displayBooleanField(value: boolean | null): boolean {
    return value == null ? false : true;
  }

  private closeModal(): void {
    this.modal?.hide();
  }

  private openFormModal(component: any) {
    this.closeModal();
    let content: any = {
      individualId: this.individualId,
      cancel: () => this.closeModal(),
      saved: () => this.closeAndRefresh()
    };

    this.modal = this.modalService.show(
      component,
      ModalSetting.createLargeModal(content)
    );
  }

  private closeAndRefresh(): void {
    this.closeModal();
    this.refresh$.next(null);
  }
}
